/**
 * SafetyReverificationBanner — persistent G3 reminder shown at the top of the
 * Pipeline Models page whenever a safety-path model was changed and a
 * vulnerable-user re-verification is still outstanding.
 *
 * One acknowledge action per pending row; acknowledging requires a note (what
 * re-verification was performed / why it's safe to clear), which is POSTed and
 * written to the reverification record.
 */

import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, TextField, Typography } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { SafetyReverification, QuillApiError } from '../../../services/quillAdminService';

export interface SafetyReverificationBannerProps {
  pending: SafetyReverification[];
  onAcknowledge: (id: string, note: string) => Promise<void>;
  notify: (message: string, severity?: 'success' | 'info' | 'error' | 'warning') => void;
}

const AcknowledgeRow: React.FC<{
  row: SafetyReverification;
  onAcknowledge: (id: string, note: string) => Promise<void>;
  notify: SafetyReverificationBannerProps['notify'];
}> = ({ row, onAcknowledge, notify }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // The backend row is keyed on pipeline_key (no display_name column); the
  // joined change log supplies new_model + changed_by + created_at.
  const label = row.pipeline_key;
  const changedAt =
    row.created_at && !Number.isNaN(new Date(row.created_at).getTime())
      ? new Date(row.created_at).toLocaleString()
      : null;

  const handleConfirm = async () => {
    if (!note.trim()) return;
    setSubmitting(true);
    try {
      await onAcknowledge(row.id, note.trim());
      notify(`Re-verification acknowledged for "${label}".`, 'success');
    } catch (e) {
      notify(
        e instanceof QuillApiError ? e.message : 'Failed to acknowledge re-verification.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={label}
          size="small"
          color="error"
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
        {row.new_model && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            now → {row.new_model}
          </Typography>
        )}
        {row.changed_by && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            changed by {row.changed_by}
          </Typography>
        )}
        {changedAt && (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {changedAt}
          </Typography>
        )}
        {!open && (
          <Button size="small" variant="outlined" color="error" onClick={() => setOpen(true)}>
            Acknowledge
          </Button>
        )}
      </Box>

      {open && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="What re-verification was performed? (required — written to the record)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 260 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={!note.trim() || submitting}
              onClick={handleConfirm}
            >
              {submitting ? 'Saving…' : 'Confirm'}
            </Button>
            <Button size="small" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const SafetyReverificationBanner: React.FC<SafetyReverificationBannerProps> = ({
  pending,
  onAcknowledge,
  notify,
}) => {
  if (!pending || pending.length === 0) return null;

  return (
    <Alert
      severity="warning"
      icon={<HealthAndSafetyIcon />}
      sx={{ mb: 3, alignItems: 'flex-start' }}
      data-testid="safety-reverification-banner"
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        A safety-path model was changed — a vulnerable-user re-verification (G3) is required
      </AlertTitle>
      <Typography variant="body2">
        {pending.length === 1
          ? 'One safety pipeline is awaiting re-verification before its change can be considered signed off.'
          : `${pending.length} safety pipelines are awaiting re-verification before their changes can be considered signed off.`}
      </Typography>
      {pending.map((row) => (
        <AcknowledgeRow key={row.id} row={row} onAcknowledge={onAcknowledge} notify={notify} />
      ))}
    </Alert>
  );
};

export default SafetyReverificationBanner;
